/**
 * Discrepancy items, grouped by severity.
 *
 * WHY shared between the payout detail and the discrepancies page: a finding reads the same way
 * wherever it appears — kind, class, amount, the engine's own explanation, and a link back to
 * the payout or line it came from. One renderer keeps that promise in both places.
 */
import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { SEVERITY_ORDER, type Discrepancy, type Severity } from "../api";
import { Amount } from "./Amount";
import { SeverityBadge } from "./Badges";
import { formatDateTime } from "../format";

export function DiscrepancyList({ items, storeId }: { items: Discrepancy[]; storeId: string }): ReactNode {
  return (
    <>
      {SEVERITY_ORDER.map((severity) => {
        const group = items.filter((d) => d.severity === severity);
        if (group.length === 0) return null;
        return (
          <div key={severity} className="severity-group">
            <h3 className="severity-head"><SeverityBadge severity={severity} /> <span className="count-chip">{group.length}</span></h3>
            <ul className="findings">
              {group.map((d) => <Finding key={d.id} item={d} storeId={storeId} />)}
            </ul>
          </div>
        );
      })}
    </>
  );
}

function Finding({ item, storeId }: { item: Discrepancy; storeId: string }): ReactNode {
  return (
    <li className={`finding finding-${toneClass(item.severity)}`}>
      <div className="finding-head">
        <span className="finding-kind">{item.kind}</span>
        {item.adjustment_class ? <span className="chip">{item.adjustment_class}</span> : null}
        {item.amount_minor !== null ? <Amount minor={item.amount_minor} currency={item.currency} signed /> : null}
      </div>
      <p className="finding-text">{item.explanation}</p>
      {item.note ? <p className="finding-note">{item.note}</p> : null}
      <p className="finding-meta">
        {item.batch_id ? (
          <Link to={`/stores/${storeId}/payouts/${item.batch_id}`} title={item.batch_external_id ?? undefined}>Payout</Link>
        ) : null}
        {item.line_external_id ? <span className="mono" title={item.line_external_id}>line</span> : null}
        {item.order_number ? <span>order {item.order_number}</span> : null}
        {item.dispute_external_id ? <Link to={`/stores/${storeId}/disputes`}>dispute</Link> : null}
        <span>first seen {formatDateTime(item.first_seen_at)}</span>
        <span>last seen {formatDateTime(item.last_seen_at)}</span>
        <span className="mono muted">{item.discrepancy_key}</span>
      </p>
    </li>
  );
}

function toneClass(severity: Severity): string {
  return severity === "ALERT" ? "bad" : severity === "WARN" ? "warn" : "info";
}
